// Import Service for State-Based Import System
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

class ImportService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/import`;
  }

  // Helper method for API calls
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }
      
      return data;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  // 1. Import Single State
  async importSingleState(stateCode, maxDealers = 100, vehiclesPerDealer = 5000) {
    return this.makeRequest('/state', {
      method: 'POST',
      body: JSON.stringify({
        stateCode,
        maxDealers,
        vehiclesPerDealer
      })
    });
  }

  // 2. Import Multiple States
  async importMultipleStates(states, maxDealersPerState = 100, vehiclesPerDealer = 5000) {
    return this.makeRequest('/states', {
      method: 'POST',
      body: JSON.stringify({
        states,
        maxDealersPerState,
        vehiclesPerDealer
      })
    });
  }

  // 3. Get State Progress
  async getStateProgress(stateCode) {
    return this.makeRequest(`/progress/state/${stateCode}`);
  }

  // 4. Get All Progress
  async getAllProgress() {
    return this.makeRequest('/progress');
  }

  // 5. Get Import Status Overview
  async getImportStatus() {
    return this.makeRequest('/status');
  }

  // 6. Clear State Checkpoint
  async clearStateCheckpoint(stateCode) {
    return this.makeRequest(`/checkpoint/state/${stateCode}`, {
      method: 'DELETE'
    });
  }

  // 7. Clear All Checkpoints
  async clearAllCheckpoints() {
    return this.makeRequest('/checkpoints', {
      method: 'DELETE'
    });
  }

  // Utility method to check if any imports are running
  async hasRunningImports() {
    try {
      const status = await this.getImportStatus();
      return status.summary.runningStates > 0;
    } catch (error) {
      console.error('Error checking running imports:', error);
      return false;
    }
  }

  // Utility method to get progress summary
  async getProgressSummary() {
    try {
      const status = await this.getImportStatus();
      return {
        totalStates: status.summary.totalStates,
        runningStates: status.summary.runningStates,
        completedStates: status.summary.completedStates,
        errorStates: status.summary.errorStates,
        totalDealersProcessed: status.summary.totalDealersProcessed,
        totalVehiclesProcessed: status.summary.totalVehiclesProcessed,
        states: status.states
      };
    } catch (error) {
      console.error('Error getting progress summary:', error);
      return {
        totalStates: 0,
        runningStates: 0,
        completedStates: 0,
        errorStates: 0,
        totalDealersProcessed: 0,
        totalVehiclesProcessed: 0,
        states: {}
      };
    }
  }
}

// Create and export singleton instance
const importService = new ImportService();
export default importService;
